import React, { useState, useEffect } from 'react';
import { Clock, GripVertical, Plus, Trash2 } from 'lucide-react';
import * as api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';

interface OvertimeRule {
  id: string;
  scope: 'company' | 'vessel' | 'role' | 'user';
  scope_id: string | null;
  scope_label: string | null;
  daily_limit_minutes: number;
  is_active: number;
}

interface User    { id: string; user_name: string; }
interface Vessel  { id: string; vessel_name: string; }

const SCOPE_META: Record<string, { label: string; color: string; bg: string }> = {
  company: { label: '会社全体',   color: '#1d4ed8', bg: '#dbeafe' },
  vessel:  { label: '船別',       color: '#059669', bg: '#d1fae5' },
  role:    { label: '役職別',     color: '#7c3aed', bg: '#ede9fe' },
  user:    { label: '個人',       color: '#dc2626', bg: '#fee2e2' },
};
const ROLE_LABELS: Record<string, string> = {
  system_admin: 'システム管理者', shore_admin: '陸上管理者', captain: '船長', crew: '乗組員',
};
const SCOPE_ORDER = ['company', 'vessel', 'role', 'user'] as const;

function fmtMin(min: number) {
  const h = Math.floor(min / 60), m = min % 60;
  return m > 0 ? `${h}時間${m}分` : `${h}時間`;
}

export function OvertimeRulesContent() {
  const [rules,    setRules]    = useState<OvertimeRule[]>([]);
  const [priority, setPriority] = useState<string[]>(['user', 'role', 'vessel', 'company']);
  const [users,    setUsers]    = useState<User[]>([]);
  const [vessels,  setVessels]  = useState<Vessel[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);

  // 新規ルール入力
  const [addScope,     setAddScope]     = useState<'company' | 'vessel' | 'role' | 'user'>('company');
  const [addScopeId,   setAddScopeId]   = useState('');
  const [addHours,     setAddHours]     = useState('8');
  const [addMin,       setAddMin]       = useState('0');
  const [addError,     setAddError]     = useState('');

  // ドラッグ状態
  const [dragIdx,  setDragIdx]  = useState<number | null>(null);
  const [overIdx,  setOverIdx]  = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [ruleRes, userRes, vesselRes] = await Promise.all([
          api.get<any>('/overtime-rules'),
          api.get<any>('/users'),
          api.get<any>('/vessels'),
        ]);
        setRules(ruleRes?.data?.rules ?? []);
        setPriority(ruleRes?.data?.priority ?? ['user', 'role', 'vessel', 'company']);
        setUsers(userRes?.data ?? []);
        setVessels(vesselRes?.data ?? vesselRes?.vessels ?? []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const scopeLabel = (r: OvertimeRule) => {
    if (r.scope === 'company') return '会社全体';
    if (r.scope === 'role')    return ROLE_LABELS[r.scope_id ?? ''] ?? r.scope_id ?? '';
    if (r.scope === 'vessel')  return vessels.find(v => v.id === r.scope_id)?.vessel_name ?? r.scope_label ?? r.scope_id ?? '';
    if (r.scope === 'user')    return users.find(u => u.id === r.scope_id)?.user_name ?? r.scope_label ?? r.scope_id ?? '';
    return '';
  };

  const handleAdd = async () => {
    setAddError('');
    const totalMin = Number(addHours) * 60 + Number(addMin);
    if (isNaN(totalMin) || totalMin < 60 || totalMin > 1440) {
      setAddError('1時間〜24時間の範囲で設定してください'); return;
    }
    if (addScope !== 'company' && !addScopeId) {
      setAddError('対象を選択してください'); return;
    }
    setSaving(true);
    try {
      let label = '';
      if (addScope === 'vessel') label = vessels.find(v => v.id === addScopeId)?.vessel_name ?? '';
      if (addScope === 'user')   label = users.find(u => u.id === addScopeId)?.user_name ?? '';
      if (addScope === 'role')   label = ROLE_LABELS[addScopeId] ?? addScopeId;
      const res = await api.post<any>('/overtime-rules', {
        scope: addScope,
        scope_id: addScope === 'company' ? undefined : addScopeId,
        scope_label: label || undefined,
        daily_limit_minutes: totalMin,
      });
      setRules(prev => {
        const exists = prev.findIndex(r => r.scope === addScope && r.scope_id === (addScope === 'company' ? null : addScopeId));
        if (exists >= 0) { const next = [...prev]; next[exists] = res.data; return next; }
        return [...prev, res.data];
      });
      setAddScopeId('');
    } catch (e: any) { setAddError(e.message ?? '保存に失敗しました'); }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.del(`/overtime-rules/${id}`);
      setRules(prev => prev.filter(r => r.id !== id));
    } catch (e: any) { alert(e.message ?? '削除に失敗しました'); }
  };

  const savePriority = async (next: string[]) => {
    setPriority(next);
    try {
      await api.put('/overtime-rules/priority', { priority: next });
    } catch (e: any) { alert(e.message ?? '優先順序の保存に失敗しました'); }
  };

  // ドラッグ並び替え
  const onDragStart = (i: number) => setDragIdx(i);
  const onDragOver  = (e: React.DragEvent, i: number) => { e.preventDefault(); setOverIdx(i); };
  const onDrop      = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...priority];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved!);
    savePriority(next);
    setDragIdx(null); setOverIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  if (loading) return <div style={{ color: '#94a3b8', fontSize: 14, padding: '16px 0' }}>読み込み中...</div>;

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ marginBottom: 28 }}>
        <p style={{ margin: '0 0 6px', fontSize: 13, color: '#64748b' }}>
          1日の標準労働時間を超えた分が「残業」として集計されます。優先度の高いルールが適用されます。
        </p>
      </div>

      {/* 優先順序 */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>適用優先順序</div>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: '#64748b' }}>
          ドラッグで並び替え。上にあるほど優先されます。
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {priority.map((scope, i) => {
            const m = SCOPE_META[scope]!;
            return (
              <div
                key={scope}
                draggable
                onDragStart={() => onDragStart(i)}
                onDragOver={e => onDragOver(e, i)}
                onDrop={e => onDrop(e, i)}
                onDragEnd={onDragEnd}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: overIdx === i ? '#eff6ff' : '#f8fafc',
                  border: `1.5px solid ${overIdx === i ? '#93c5fd' : '#e2e8f0'}`,
                  borderRadius: 8, padding: '10px 14px', cursor: 'grab',
                  opacity: dragIdx === i ? 0.4 : 1, transition: 'opacity 0.15s',
                }}
              >
                <GripVertical size={16} style={{ color: '#94a3b8', flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#64748b', minWidth: 20 }}>{i + 1}</span>
                <span style={{ background: m.bg, color: m.color, borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>
                  {m.label}
                </span>
                {rules.some(r => r.scope === scope) ? (
                  <span style={{ fontSize: 12, color: '#059669', marginLeft: 4 }}>● 設定済み</span>
                ) : (
                  <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 4 }}>未設定（スキップ）</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ルール一覧 */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>設定済みルール</div>
        {rules.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13, padding: '12px 0' }}>ルールが未設定のため、デフォルト 8時間 が適用されます。</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['スコープ', '対象', '1日の基準時間', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rules.map((r, i) => {
                const m = SCOPE_META[r.scope]!;
                return (
                  <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: m.bg, color: m.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>{m.label}</span>
                    </td>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: '#0f172a' }}>{scopeLabel(r)}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: '#0f172a', fontSize: 15 }}>
                      {fmtMin(r.daily_limit_minutes)}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                      <button onClick={() => handleDelete(r.id)}
                        style={{ padding: '5px 10px', border: '1px solid #fecaca', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                        <Trash2 size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />削除
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* 新規追加 */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>
          <Plus size={15} style={{ verticalAlign: 'middle', marginRight: 4 }} />ルールを追加
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600 }}>
            スコープ
            <select value={addScope} onChange={e => { setAddScope(e.target.value as any); setAddScopeId(''); }}
              style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', background: '#fff', minWidth: 120 }}>
              <option value="company">会社全体</option>
              <option value="vessel">船別</option>
              <option value="role">役職別</option>
              <option value="user">個人</option>
            </select>
          </label>

          {addScope !== 'company' && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600 }}>
              対象
              {addScope === 'user' ? (
                <div style={{ minWidth: 220 }}>
                  <SearchableSelect
                    value={addScopeId}
                    onChange={setAddScopeId}
                    options={users.map(u => ({ value: u.id, label: u.user_name }))}
                    placeholder="氏名で検索..."
                  />
                </div>
              ) : (
                <select value={addScopeId} onChange={e => setAddScopeId(e.target.value)}
                  style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', background: '#fff', minWidth: 160 }}>
                  <option value="">— 選択 —</option>
                  {addScope === 'vessel' && vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
                  {addScope === 'role'   && Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              )}
            </label>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600 }}>
            基準時間
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="number" value={addHours} onChange={e => setAddHours(e.target.value)} min={1} max={24}
                style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 14, outline: 'none', width: 60, textAlign: 'right' }} />
              <span style={{ fontSize: 13, color: '#374151' }}>時間</span>
              <input type="number" value={addMin} onChange={e => setAddMin(e.target.value)} min={0} max={59} step={15}
                style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 14, outline: 'none', width: 60, textAlign: 'right' }} />
              <span style={{ fontSize: 13, color: '#374151' }}>分</span>
            </div>
          </label>

          <button onClick={handleAdd} disabled={saving}
            style={{ padding: '10px 22px', border: 'none', borderRadius: 8, background: saving ? '#93c5fd' : '#1d4ed8', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, alignSelf: 'flex-end' }}>
            {saving ? '保存中...' : '追加'}
          </button>
        </div>
        {addError && <div style={{ marginTop: 10, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>{addError}</div>}
        <div style={{ marginTop: 14, padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, fontSize: 12, color: '#0369a1' }}>
          同じスコープ・対象のルールは上書きされます。ルール未設定のスコープは優先順序でスキップされ、最終的にデフォルト 8時間 が適用されます。
        </div>
      </div>
    </div>
  );
}

export default function OvertimeRulesPage() {
  return <OvertimeRulesContent />;
}
